import type {
  ContainerMetric,
  ContainerMetricReason,
  ContainerMetricSample,
  ContainerMetricSnapshot,
  ContainerMetricState,
  Section,
} from "@k8s-dashboard/contracts";
import { Panel } from "@/components/primitives";
import { humanBytes, humanCores } from "@/lib/format";
import { useT, type Translate } from "@/i18n/I18nProvider";

const KIND_MESSAGE: Record<ContainerMetric["kind"], string> = {
  app: "pod.kind.app", init: "pod.kind.init", ephemeral: "pod.kind.ephemeral",
};

export const CONTAINER_METRIC_STATE_LABEL: Record<ContainerMetricState, string> = {
  ok: "common.node.telemetry.state.ok",
  warming: "common.node.telemetry.state.warming",
  missing: "common.node.telemetry.state.missing",
  degraded: "common.node.telemetry.state.degraded",
};

export const CONTAINER_METRIC_REASON_LABEL = {
  not_running: "pod.mr.notRunning",
  rate_window: "pod.mr.rateWindow",
  series_absent: "pod.mr.absent",
  sample_before_pod: "pod.mr.beforePod",
  sample_future: "pod.mr.future",
  sample_stale: "pod.mr.stale",
  cpu_layout_unsupported: "pod.mr.cpuUnsupported",
  cpu_layout_ambiguous: "pod.mr.cpuAmbiguous",
  identity_ambiguous: "pod.mr.idAmbiguous",
  identity_mismatch: "pod.mr.idMismatch",
  identity_malformed: "pod.mr.idMalformed",
  pair_broken: "common.node.telemetry.reason.pair_broken",
  point_budget_exceeded: "pod.mr.sampleLimit",
  upstream_unavailable: "pod.mr.unavailable",
  upstream_invalid: "common.node.telemetry.reason.upstream_invalid",
  result_too_large: "pod.mr.sizeLimit",
  query_unsupported: "pod.mr.unsupported",
} as const satisfies Record<ContainerMetricReason, string>;

function metricMessage(id: string, podT: Translate, commonT: Translate): string {
  return id.startsWith("common.") ? commonT(id) : podT(id);
}

function sampleText(sample: ContainerMetricSample, family: "cpu" | "memory", podT: Translate, commonT: Translate): string {
  if (sample.state !== "ok" || sample.value === undefined) {
    const state = sample.state === "ok" ? "degraded" : sample.state;
    const reason = sample.reasonCode ?? (sample.state === "ok" ? "upstream_invalid" : "series_absent");
    return podT("pod.metrics.status", {
      state: commonT(CONTAINER_METRIC_STATE_LABEL[state]),
      reason: metricMessage(CONTAINER_METRIC_REASON_LABEL[reason], podT, commonT),
    });
  }
  const value = family === "cpu" ? humanCores(sample.value) : humanBytes(sample.value);
  const observed = sample.sampleTime
    ? podT("pod.metrics.sample", { time: sample.sampleTime, age: sample.sampleAgeSeconds ?? 0 })
    : podT("pod.metrics.status", {
        state: commonT(CONTAINER_METRIC_STATE_LABEL.degraded),
        reason: commonT(CONTAINER_METRIC_REASON_LABEL.upstream_invalid),
      });
  return `${value} · ${podT("pod.metrics.statusOk", { state: commonT(CONTAINER_METRIC_STATE_LABEL.ok) })} · ${observed}`;
}

export function PodContainerMetricsCard({ section, referenceIso }: { section?: Section<ContainerMetricSnapshot>; referenceIso?: string }) {
  const { t } = useT("pod");
  const { t: commonT } = useT("common");
  if (!section) return null;
  if (section.status === "degraded" && !section.data) {
    return (
      <Panel title={t("pod.metrics.title")} subtitle={t("pod.metrics.scope")} section={section} referenceIso={referenceIso}>
        <div className="state state--degraded" role="note" data-state={section.reason}>
          <span className="state__glyph" aria-hidden="true">▲</span>
          <span className="state__title">{section.reason === "container_metrics_unsupported"
            ? t("pod.metrics.section.unsupported")
            : commonT(CONTAINER_METRIC_STATE_LABEL.degraded)}</span>
        </div>
      </Panel>
    );
  }
  if (!section.data) return null;
  return (
    <Panel title={t("pod.metrics.title")} subtitle={t("pod.metrics.scope")} section={section} referenceIso={referenceIso} flush>
      <p className="muted">{t("pod.metrics.explanation")}</p>
      <div className="panel__scroll" tabIndex={0} role="group" aria-label={t("pod.metrics.title")}>
        <table className="ds-data-table ds-data-table--compact">
          <thead><tr>
            <th scope="col">{t("pod.kind")}</th><th scope="col">{t("pod.name")}</th>
            <th scope="col">{t("pod.metrics.cpu")}</th><th scope="col">{t("pod.metrics.memory")}</th>
          </tr></thead>
          <tbody>{section.data.containers.map((row) => (
            <tr key={`${row.kind}-${row.name}`}>
              <td>{t(KIND_MESSAGE[row.kind])}</td><td className="ds-ident">{row.name}</td>
              <td>{sampleText(row.cpu, "cpu", t, commonT)}</td><td>{sampleText(row.memory, "memory", t, commonT)}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </Panel>
  );
}
